import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../../base/AdminContext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Password } from 'primereact/password';
import { InputText } from 'primereact/inputtext';
const AdminLoginPage: React.FC = () => {
    const [formData, setFormData] = useState({
        user_name: '',
        pass_word: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [rememberMe, setRememberMe] = useState(false);

    const { login, isAuthenticated } = useAdmin();
    const location = useLocation();

    // Redirect nếu đã đăng nhập
    if (isAuthenticated) {
        const from = (location.state as any)?.from?.pathname || '/admin/dashboard';
        return <Navigate to={from} replace />;
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
        const name = e.target?.name || e.originalEvent?.target?.name;
        const value = e.target?.value !== undefined ? e.target.value : e.value;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Xóa error khi user bắt đầu nhập
        if (error) {
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        if (!formData.user_name.trim()) {
            setError('Vui lòng nhập tên đăng nhập');
            return;
        }

        if (!formData.pass_word.trim()) {
            setError('Vui lòng nhập mật khẩu');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const success = await login(formData);

            if (!success) {
                setError('Tên đăng nhập hoặc mật khẩu không đúng');
            }
            // Nếu thành công, useAdmin sẽ tự động redirect
        } catch (err: any) {
            setError(err.message || 'Có lỗi xảy ra khi đăng nhập');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex align-items-center justify-content-center" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '2rem 1rem'
        }}>
            <div className="surface-card p-6 shadow-8 border-round-lg w-25rem sm:w-30rem">
                {/* Header Section */}
                <div className="text-center mb-6">
                    <div className="mb-4">
                        <i className="pi pi-shield text-6xl text-primary"></i>
                    </div>
                    <h1 className="text-900 text-3xl font-bold mb-2">Đăng nhập Admin</h1>
                    <p className="text-600 font-medium line-height-3">
                        Hệ thống quản lý gọi xe
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="p-3 mb-4 border-round-md" style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626'
                    }}>
                        <div className="flex align-items-center">
                            <i className="pi pi-exclamation-triangle mr-2"></i>
                            <span className="text-sm font-medium">{error}</span>
                        </div>
                    </div>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="flex flex-column gap-4">
                    {/* Username Field */}
                    <div className="field">
                        <label htmlFor="user_name" className="block text-900 font-semibold mb-2">
                            <i className="pi pi-user mr-2"></i>
                            Tên đăng nhập
                        </label>
                        <InputText
                            id="user_name"
                            name="user_name"
                            value={formData.user_name}
                            onChange={handleInputChange}
                            placeholder="Nhập tên đăng nhập"
                            className="w-full p-3"
                            disabled={isLoading}
                            autoComplete="username"
                        />
                    </div>

                    {/* Password Field */}
                    <div className="field">
                        <label htmlFor="pass_word" className="block text-900 font-semibold mb-2">
                            <i className="pi pi-lock mr-2"></i>
                            Mật khẩu
                        </label>
                        <Password
                            id="pass_word"
                            name="pass_word"
                            value={formData.pass_word}
                            onChange={handleInputChange}
                            placeholder="Nhập mật khẩu"
                            className="w-full"
                            inputClassName="w-full p-3"
                            disabled={isLoading}
                            feedback={false}
                            toggleMask
                            autoComplete="current-password"
                        />
                    </div>

                    {/* Remember Me & Forgot Password */}
                    <div className="flex align-items-center justify-content-between mb-2">
                        <div className="flex align-items-center">
                            <Checkbox
                                id="rememberme"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.checked || false)}
                                className="mr-2"
                                disabled={isLoading}
                            />
                            <label htmlFor="rememberme" className="text-900 font-medium">
                                Ghi nhớ đăng nhập
                            </label>
                        </div>
                        <button
                            type="button"
                            className="font-medium no-underline text-primary cursor-pointer bg-transparent border-none p-0"
                            style={{ textDecoration: 'none' }}
                        >
                            Quên mật khẩu?
                        </button>
                    </div>

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        label={isLoading ? "Đang đăng nhập..." : "Đăng nhập"}
                        icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-sign-in"}
                        className="w-full p-3 text-lg font-semibold"
                        disabled={isLoading}
                        loading={isLoading}
                    />
                </form>

                {/* Footer */}
                <div className="text-center mt-6 pt-4 border-top-1 surface-border">
                    <div className="text-600 text-sm flex align-items-center justify-content-center">
                        <i className="pi pi-shield mr-2"></i>
                        <span>Bảo mật và an toàn</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
